import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import CameraLoader from "./components/CameraLoader";
import API_BASE_URL from "./config";
import "./PdfReview.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const COLORS = ["#ff0000", "#0066ff", "#00cc44", "#ff9900", "#9900ff", "#000000"];

const LOG_PREFIX = "[PdfReview]";

function PdfReview() {
  const { token } = useParams();
  const [event, setEvent] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reviewLocked, setReviewLocked] = useState(false);
  const [error, setError] = useState("");
  const [clientName, setClientName] = useState("");
  const [studioName, setStudioName] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedMsg, setDraftSavedMsg] = useState("");
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [studioLogo, setStudioLogo] = useState("");

  // Per-page state
  const [pageComments, setPageComments] = useState({});
  const [pageDrawings, setPageDrawings] = useState({});

  // Drawing
  const [drawColor, setDrawColor] = useState(COLORS[0]);
  const [brushSize, setBrushSize] = useState(3);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isPageRendering, setIsPageRendering] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);

  const pdfCanvasRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const pdfDocRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const renderTokenRef = useRef(0);
  const pageDrawingsRef = useRef({});
  const pageBitmapRef = useRef({});

  const debugLog = useCallback((...args) => {
    // Keep logs always on for this active debug cycle.
    console.log(LOG_PREFIX, ...args);
  }, []);

  // Load draft from DB (reusable)
  const loadDraftFromDB = useCallback(async () => {
    try {
      const draftRes = await fetch(`${API_BASE_URL}/api/shared/${token}/pdf/draft`);
      const draftData = await draftRes.json();
      if (draftData.draft) {
        const d = typeof draftData.draft.draft_data === "string"
          ? JSON.parse(draftData.draft.draft_data)
          : draftData.draft.draft_data;
        if (d.clientName) setClientName(d.clientName);
        if (d.pageComments) setPageComments(d.pageComments);
        if (d.pageDrawings) setPageDrawings(d.pageDrawings);
        debugLog("Draft restored from DB", { pages: Object.keys(d.pageComments || {}).length });
      }
    } catch (e) { debugLog("Draft load failed", e); }
  }, [token, debugLog]);

  // Fetch PDF data + check status + load draft
  useEffect(() => {
    (async () => {
      try {
        debugLog("Fetching shared PDF", { token });
        const res = await fetch(`${API_BASE_URL}/api/shared/${token}/pdf`);
        const data = await res.json();
        if (!res.ok) { setError(data.message || "Failed to load"); setLoading(false); return; }
        setEvent(data.event);
        setPdfUrl(data.pdf.pdf_url);
        setStudioName(data.event?.studio_name || "");
        setStudioLogo(data.event?.logo_url || "");
        debugLog("Shared PDF fetched", { pageCount: data?.pdf?.page_count, pdfUrl: data?.pdf?.pdf_url, studio: data.event?.studio_name });

        // Check if already submitted/locked
        try {
          const statusRes = await fetch(`${API_BASE_URL}/api/shared/${token}/pdf/status`);
          const statusData = await statusRes.json();
          if (statusData.submitted) {
            setSubmitted(true);
            setReviewLocked(statusData.locked || false);
          }
        } catch (e) { debugLog("Status check failed", e); }

        // Load draft from DB
        if (!submitted) {
          await loadDraftFromDB();
        }
      } catch (err) {
        debugLog("Fetch shared PDF failed", err);
        setError("Failed to load PDF review");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, debugLog, loadDraftFromDB]);

  // Poll status when submitted — auto-reset to draft if studio unlocks
  useEffect(() => {
    if (!submitted) return;
    const interval = setInterval(async () => {
      try {
        const statusRes = await fetch(`${API_BASE_URL}/api/shared/${token}/pdf/status`);
        const statusData = await statusRes.json();
        debugLog("Status poll", statusData);
        if (!statusData.submitted) {
          // Studio has unlocked — reset to draft mode
          debugLog("Review unlocked by studio, resetting to draft");
          setSubmitted(false);
          setReviewLocked(false);
          await loadDraftFromDB();
        }
      } catch (e) { debugLog("Status poll failed", e); }
    }, 5000);
    return () => clearInterval(interval);
  }, [submitted, token, debugLog, loadDraftFromDB]);

  const replayStrokes = useCallback((canvas, strokes) => {
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let applied = 0;
    for (const stroke of strokes) {
      if (!stroke || !stroke.color || !Array.isArray(stroke.points) || stroke.points.length === 0) {
        continue;
      }
      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.size;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (let i = 0; i < stroke.points.length; i++) {
        const p = stroke.points[i];
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
      applied += 1;
    }
    return applied;
  }, []);

  const renderPage = useCallback(async (pageNum, doc) => {
    const pdfDoc = doc || pdfDocRef.current;
    if (!pdfDoc) return;
    debugLog("Render start", { pageNum, hasDoc: !!pdfDoc });

    // Cancel any in-flight render before starting a new one on the same canvas.
    if (renderTaskRef.current) {
      try { renderTaskRef.current.cancel(); debugLog("Cancelled in-flight render", { pageNum }); } catch (e) { /* ignore */ }
      renderTaskRef.current = null;
    }
    const renderToken = ++renderTokenRef.current;

    const page = await pdfDoc.getPage(pageNum);
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = Math.min(container.clientWidth - 32, 900);
    const viewport = page.getViewport({ scale: 1 });
    const scale = containerWidth / viewport.width;
    const scaledViewport = page.getViewport({ scale });

    const pdfCanvas = pdfCanvasRef.current;
    const drawCanvas = drawCanvasRef.current;
    if (!pdfCanvas || !drawCanvas) return;

    pdfCanvas.width = scaledViewport.width;
    pdfCanvas.height = scaledViewport.height;
    drawCanvas.width = scaledViewport.width;
    drawCanvas.height = scaledViewport.height;

    const ctx = pdfCanvas.getContext("2d");
    const renderTask = page.render({
      canvasContext: ctx,
      viewport: scaledViewport,
      // Some PDFs contain malformed annotation color objects (null),
      // which can crash pdf.js render. Disable annotation/form layer rendering.
      annotationMode: pdfjsLib.AnnotationMode.DISABLE,
      renderInteractiveForms: false,
    });
    renderTaskRef.current = renderTask;
    setIsPageRendering(true);

    try {
      await renderTask.promise;
      debugLog("Render finished", { pageNum });
    } catch (err) {
      if (err?.name !== "RenderingCancelledException") {
        debugLog("Render failed", { pageNum, err: err?.message || String(err) });
        throw err;
      }
      debugLog("Render cancelled", { pageNum });
      return;
    } finally {
      if (renderTaskRef.current === renderTask) {
        renderTaskRef.current = null;
      }
      setIsPageRendering(false);
    }

    // Ignore stale render completions.
    if (renderToken !== renderTokenRef.current) return;

    // Restore saved drawing overlay for this page.
    const bitmap = pageBitmapRef.current[pageNum];
    if (bitmap) {
      const drawCtx = drawCanvas.getContext("2d");
      const img = new Image();
      img.onload = () => {
        drawCtx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
        drawCtx.drawImage(img, 0, 0, drawCanvas.width, drawCanvas.height);
        debugLog("Overlay restored from bitmap", { pageNum });
      };
      img.onerror = () => {
        const saved = pageDrawingsRef.current[pageNum] || [];
        const applied = replayStrokes(drawCanvas, saved);
        debugLog("Bitmap restore failed, replayed strokes", { pageNum, applied });
      };
      img.src = bitmap;
    } else {
      const saved = pageDrawingsRef.current[pageNum] || [];
      const applied = replayStrokes(drawCanvas, saved);
      debugLog("Overlay restored from strokes", { pageNum, strokes: saved.length, applied });
    }
  }, [replayStrokes, debugLog]);

  // Load PDF document
  useEffect(() => {
    if (!pdfUrl) return;
    (async () => {
      try {
        const doc = await pdfjsLib.getDocument(pdfUrl).promise;
        pdfDocRef.current = doc;
        setPageCount(doc.numPages);
        debugLog("PDF loaded", { numPages: doc.numPages });
        await renderPage(1, doc);
        setPdfReady(true);
      } catch (err) {
        debugLog("PDF load/render failed", err);
        setError("Failed to render PDF: " + err.message);
      }
    })();
    return () => {
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch (e) { /* ignore */ }
      }
    };
  }, [pdfUrl, renderPage, debugLog]);

  // Re-render on page change
  useEffect(() => {
    if (pageCount > 0 && pdfDocRef.current) {
      debugLog("Page changed", { currentPage });
      renderPage(currentPage);
    }
  }, [currentPage, pageCount, renderPage, debugLog]);

  // Keep latest drawings in a ref so renderPage uses fresh data without re-rendering PDF on every stroke.
  useEffect(() => {
    pageDrawingsRef.current = pageDrawings;
    const drawCanvas = drawCanvasRef.current;
    if (!drawCanvas) return;
    const saved = pageDrawings[currentPage] || [];
    const applied = replayStrokes(drawCanvas, saved);
    debugLog("Replay on state update", { currentPage, strokes: saved.length, applied });
  }, [pageDrawings, currentPage, replayStrokes]);

  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch (e) { /* ignore */ }
      }
    };
  }, []);

  // Drawing handlers
  const getPos = (e) => {
    const canvas = drawCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const currentStrokeRef = useRef(null);

  const startDraw = (e) => {
    if (isPageRendering) return;
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPos(e);
    debugLog("Stroke start", { page: currentPage, x: Math.round(pos.x), y: Math.round(pos.y), color: drawColor, size: brushSize });
    currentStrokeRef.current = { color: drawColor, size: brushSize, points: [pos] };
    const drawCanvas = drawCanvasRef.current;
    if (!drawCanvas) return;
    const ctx = drawCanvas.getContext("2d");
    ctx.beginPath();
    ctx.strokeStyle = drawColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    if (!currentStrokeRef.current || !Array.isArray(currentStrokeRef.current.points)) return;
    e.preventDefault();
    const pos = getPos(e);
    currentStrokeRef.current.points.push(pos);
    const drawCanvas = drawCanvasRef.current;
    if (!drawCanvas) return;
    const ctx = drawCanvas.getContext("2d");
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (currentStrokeRef.current && currentStrokeRef.current.points.length > 1) {
      const stroke = currentStrokeRef.current;
      setPageDrawings((prev) => ({
        ...prev,
        [currentPage]: [...(prev[currentPage] || []), stroke],
      }));

      const canvas = drawCanvasRef.current;
      if (canvas) {
        try {
          pageBitmapRef.current[currentPage] = canvas.toDataURL("image/png");
          debugLog("Stroke committed", { page: currentPage, points: stroke.points.length, totalStrokes: (pageDrawingsRef.current[currentPage] || []).length + 1 });
        } catch (err) {
          debugLog("Bitmap save failed", err);
        }
      }
    }
    currentStrokeRef.current = null;
  };

  const clearPage = () => {
    const canvas = drawCanvasRef.current;
    if (canvas) {
      canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    }
    delete pageBitmapRef.current[currentPage];
    setPageDrawings((prev) => ({ ...prev, [currentPage]: [] }));
    debugLog("Page cleared", { page: currentPage });
  };

  const handleCommentChange = (val) => {
    setPageComments((prev) => ({ ...prev, [currentPage]: val }));
  };

  const goToPage = (num) => {
    if (num < 1 || num > pageCount) return;
    setCurrentPage(num);
  };

  // Save draft to DB
  const saveDraft = async () => {
    if (reviewLocked) return;
    setSavingDraft(true);
    setDraftSavedMsg("");
    try {
      const draftData = { clientName, pageComments, pageDrawings };
      const res = await fetch(`${API_BASE_URL}/api/shared/${token}/pdf/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientName, draftData }),
      });
      if (res.ok) {
        setDraftSavedMsg("Draft saved!");
        setTimeout(() => setDraftSavedMsg(""), 3000);
        debugLog("Draft saved to DB");
      } else {
        const data = await res.json();
        setError(data.message || "Failed to save draft");
      }
    } catch (err) {
      setError("Failed to save draft");
      debugLog("Draft save error", err);
    } finally {
      setSavingDraft(false);
    }
  };

  // Confirm before submit
  const confirmSubmit = () => {
    const pages = [];
    for (let i = 1; i <= pageCount; i++) {
      const comment = pageComments[i] || "";
      const drawing = pageDrawings[i] || [];
      if (comment || drawing.length > 0) pages.push(i);
    }
    if (pages.length === 0) { setError("Please add at least one comment or drawing"); return; }
    setError("");
    setShowConfirmSubmit(true);
  };

  const handleSubmit = async () => {
    setShowConfirmSubmit(false);
    setSubmitting(true);
    setError("");
    try {
      const pages = [];
      for (let i = 1; i <= pageCount; i++) {
        const comment = pageComments[i] || "";
        const drawing = pageDrawings[i] || [];
        if (comment || drawing.length > 0) {
          pages.push({ pageNumber: i, comment, drawing });
        }
      }

      if (pages.length === 0) { setError("Please add at least one comment or drawing"); setSubmitting(false); return; }

      const res = await fetch(`${API_BASE_URL}/api/shared/${token}/pdf/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientName: "Guest", pages }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Submission failed"); setSubmitting(false); return; }
      setSubmitted(true);
      setReviewLocked(true);
    } catch (err) {
      setError("Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pr-loading-page">
        <CameraLoader text="Loading PDF review..." />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="pr-success-page">
        <div className="pr-success-card">
          <span className="pr-success-icon">✅</span>
          <h2>Review Submitted!</h2>
          <p>Your feedback has been sent to the studio.</p>
          {reviewLocked && (
            <p className="pr-locked-notice">🔒 Your review is now locked. Contact the studio to make changes.</p>
          )}
          <p className="pr-success-event">{event?.event_name}</p>
          {studioName && <p className="pr-success-studio">— {studioName}</p>}
          <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '16px' }}>This page will automatically update if the studio unlocks your review.</p>
        </div>
      </div>
    );
  }

  if (error && !pdfUrl) {
    return (
      <div className="pr-error-page">
        <h2>😕 {error}</h2>
        <p>This link may be invalid or the PDF hasn't been uploaded yet.</p>
      </div>
    );
  }

  return (
    <div className="pr-container" ref={containerRef}>
      {/* Header with studio branding */}
      <header className="pr-header">
        <div className="pr-studio-brand">
          {studioLogo ? (
            <img src={studioLogo} alt="Logo" className="pr-studio-logo" />
          ) : studioName ? (
            <div className="pr-studio-logo-placeholder">
              {studioName.charAt(0).toUpperCase()}
            </div>
          ) : null}
          {studioName && <span className="pr-studio-name">{studioName}</span>}
        </div>
        <div className="pr-header-info">
          <h1 className="pr-title">{event?.event_name || "PDF Review"}</h1>
          <p className="pr-subtitle">
            {event?.groom_name} & {event?.bridal_name}
            {event?.event_date && ` • ${new Date(event.event_date).toLocaleDateString()}`}
          </p>
        </div>
      </header>

      {error && <div className="pr-error-msg">{error}</div>}

      {/* Confirm submit popup */}
      {showConfirmSubmit && (
        <div className="pr-confirm-overlay">
          <div className="pr-confirm-card">
            <h3>⚠️ Submit Review?</h3>
            <p>Once submitted, you <strong>cannot resubmit again</strong>. Your review will be locked and no further changes can be made unless the studio unlocks it.</p>
            <p style={{ color: '#fbbf24', fontSize: '13px', marginTop: '8px' }}>Please make sure all your feedback is complete before submitting.</p>
            <div className="pr-confirm-actions">
              <button className="pr-confirm-cancel" onClick={() => setShowConfirmSubmit(false)}>Cancel</button>
              <button className="pr-confirm-submit" onClick={handleSubmit} disabled={submitting}>
                {submitting ? "Submitting..." : "Yes, Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Name input removed */}

      {/* Page Navigation */}
      <div className="pr-page-nav">
        <button className="pr-nav-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}>
          ← Prev
        </button>
        <span className="pr-page-info">Page {currentPage} of {pageCount}</span>
        <button className="pr-nav-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= pageCount}>
          Next →
        </button>
      </div>

      {/* Drawing Tools */}
      {!reviewLocked && (
        <div className="pr-tools">
          <div className="pr-colors">
            {COLORS.map((c) => (
              <button
                key={c}
                className={`pr-color-btn ${drawColor === c ? "active" : ""}`}
                style={{ backgroundColor: c }}
                onClick={() => setDrawColor(c)}
              />
            ))}
          </div>
          <div className="pr-brush-size">
            <label>Size:</label>
            <input
              type="range"
              min="1"
              max="10"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
            />
          </div>
          <button className="pr-clear-btn" onClick={clearPage}>🗑 Clear Drawing</button>
        </div>
      )}

      {/* PDF + Drawing Canvas */}
      <div className="pr-canvas-wrapper">
        {!pdfReady && (
          <div className="pr-pdf-loader-overlay">
            <CameraLoader text="Loading PDF..." />
          </div>
        )}
        <canvas ref={pdfCanvasRef} className="pr-pdf-canvas" />
        <canvas
          ref={drawCanvasRef}
          className={`pr-draw-canvas ${isPageRendering || reviewLocked ? "disabled" : ""}`}
          onMouseDown={reviewLocked ? undefined : startDraw}
          onMouseMove={reviewLocked ? undefined : draw}
          onMouseUp={reviewLocked ? undefined : endDraw}
          onMouseLeave={reviewLocked ? undefined : endDraw}
          onTouchStart={reviewLocked ? undefined : startDraw}
          onTouchMove={reviewLocked ? undefined : draw}
          onTouchEnd={reviewLocked ? undefined : endDraw}
        />
        {pdfReady && isPageRendering && <div className="pr-rendering-overlay">Rendering page...</div>}
      </div>

      {/* Comment textarea */}
      <div className="pr-comment-section">
        <label className="pr-comment-label">💬 Feedback for Page {currentPage}</label>
        <textarea
          className="pr-comment-input"
          placeholder={reviewLocked ? "Review is locked" : `Write your feedback for page ${currentPage}...`}
          value={pageComments[currentPage] || ""}
          onChange={(e) => handleCommentChange(e.target.value)}
          rows={3}
          disabled={reviewLocked}
        />
      </div>

      {/* Page dots */}
      <div className="pr-page-dots">
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => {
          const hasContent = (pageComments[p] && pageComments[p].trim()) || (pageDrawings[p] && pageDrawings[p].length > 0);
          return (
            <button
              key={p}
              className={`pr-dot ${p === currentPage ? "current" : ""} ${hasContent ? "has-content" : ""}`}
              onClick={() => goToPage(p)}
              title={`Page ${p}`}
            >
              {p}
            </button>
          );
        })}
      </div>

      {/* Submit + Save Draft */}
      {!reviewLocked && (
        <div className="pr-submit-section">
          <div className="pr-submit-row">
            <button
              className="pr-draft-btn"
              onClick={saveDraft}
              disabled={savingDraft || submitting}
            >
              {savingDraft ? "Saving..." : "💾 Save Draft"}
            </button>
            <button
              className="pr-submit-btn"
              onClick={confirmSubmit}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "✅ Submit Review"}
            </button>
          </div>
          {draftSavedMsg && <p className="pr-draft-msg">{draftSavedMsg}</p>}
        </div>
      )}
      {reviewLocked && (
        <div className="pr-locked-banner">
          🔒 Your review has been submitted and is locked. Contact the studio to make changes.
        </div>
      )}
    </div>
  );
}

export default PdfReview;
